import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { AutoAnalysisService } from './AutoAnalysisService';
import { BlockedUrlService } from './BlockedUrlService';

/**
 * Service pour gérer les essais gratuits
 * Utilise uniquement la table prospects avec un flag trial dans metadata
 */
export const TrialService = {
  /**
   * Démarre le processus d'essai
   * @param websiteUrl URL du site web
   * @param email Email professionnel
   * @param companyName Nom de l'entreprise
   * @param domain Domaine extrait
   * @param verificationToken Token de vérification
   */
  startTrialProcess: async (
    websiteUrl: string,
    email: string,
    companyName: string,
    city: string,
    domain: string,
    verificationToken: string
  ): Promise<{ success: boolean; message: string; prospectId?: string; accessToken?: string }> => {
    try {
      // Autoriser l'adresse email mehdi@smart-impact.ch à être utilisée plusieurs fois pour les tests
      const isTestEmail = email.toLowerCase() === 'mehdi@smart-impact.ch';
      
      // 🔒 VÉRIFICATION 1 : Bloquer les URLs de clients existants
      if (!isTestEmail) {
        const blockCheck = await BlockedUrlService.isUrlBlocked(websiteUrl);
        
        if (blockCheck.isBlocked) {
          console.log('🚫 [TrialService] URL bloquée détectée:', {
            url: websiteUrl,
            reason: blockCheck.reason,
            blockedUrl: blockCheck.blockedUrl
          });
          
          return {
            success: false,
            message: "Cette URL fait partie de nos clients existants. Pour toute question, contactez-nous directement à info@smart-impact.ch"
          };
        }
      }
      
      // 🔒 VÉRIFICATION 2 : Vérifier si l'email ou le domaine existe déjà dans un essai récent
      // Sauf pour l'email de test
      if (!isTestEmail) {
        const { count } = await supabase
          .from('prospects')
          .select('*', { count: 'exact', head: true })
          .or(`email.eq.${email},site_url.eq.${websiteUrl}`)
          .gt('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
        
        if (count && count > 0) {
          return {
            success: false,
            message: "Un essai existe déjà pour cet email ou ce site web. Veuillez réessayer dans 7 jours."
          };
        }
      }
      
      // Pour l'email de test, on supprime d'abord les anciennes entrées pour éviter l'accumulation
      if (isTestEmail) {
        
        // Recherche des prospects existants avec cet email
        const { data: existingProspects } = await supabase
          .from('prospects')
          .select('id')
          .eq('email', email)
          .order('created_at', { ascending: false })
          .limit(10);
          
        // Garder seulement les 3 plus récents pour éviter une accumulation excessive
        if (existingProspects && existingProspects.length > 3) {
          const prospectsToDelete = existingProspects.slice(3);
          
          for (const prospect of prospectsToDelete) {
            // Supprimer d'abord les données liées
            await supabase.from('pagespeed_results').delete().eq('prospect_id', prospect.id);
            await supabase.from('keyword_analysis_results').delete().eq('prospect_id', prospect.id);
            await supabase.from('cms_detection_results').delete().eq('prospect_id', prospect.id);
            await supabase.from('personalized_intros').delete().eq('prospect_id', prospect.id);
            await supabase.from('google_reviews_results').delete().eq('prospect_id', prospect.id);
            await supabase.from('ai_recommendations').delete().eq('prospect_id', prospect.id);
            await supabase.from('ai_citations_results').delete().eq('prospect_id', prospect.id);
            
            // Puis supprimer le prospect
            await supabase.from('prospects').delete().eq('id', prospect.id);
            
          }
        }
      }
      
      // Générer directement un token d'accès
      const accessToken = uuidv4();
      const prospectId = uuidv4();
      
      // Créer un nouveau prospect avec flag trial
      const { error } = await supabase
        .from('prospects')
        .insert({
          id: prospectId,
          prenom: '',
          nom: companyName,
          email: email,
          site_url: websiteUrl,
          token: accessToken,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          metadata: {
            is_trial: true,
            verification_token: verificationToken,
            source: 'self-serve',
            company: companyName,
            domain: domain,
            location: city
          }
        });
        
      if (error) {
        console.error("Erreur lors de la création du prospect:", error);
        throw error;
      }
      
      // Lancer automatiquement les analyses et ATTENDRE qu'elles se terminent
      // L'utilisateur verra un écran de chargement pendant ce temps
      console.log('🔄 [TrialService] Lancement des analyses automatiques...');
      const analysisStartTime = Date.now();

      try {
        const success = await AutoAnalysisService.runAllAnalyses(prospectId, websiteUrl, companyName, city);
        const analysisDuration = ((Date.now() - analysisStartTime) / 1000).toFixed(2);
        console.log(`✅ [TrialService] Analyses terminées en ${analysisDuration}s - Succès:`, success);
      } catch (error) {
        const analysisDuration = ((Date.now() - analysisStartTime) / 1000).toFixed(2);
        console.error(`❌ [TrialService] Erreur après ${analysisDuration}s:`, error);
        // Continuer même si certaines analyses échouent
      }

      console.log('🎯 [TrialService] Processus terminé, redirection vers les résultats');
      return {
        success: true,
        message: "Essai créé avec succès",
        prospectId,
        accessToken
      };
    } catch (error) {
      console.error("Erreur lors du démarrage de l'essai:", error);
      return {
        success: false,
        message: "Une erreur est survenue lors du démarrage de l'essai. Veuillez réessayer."
      };
    }
  },

  /**
   * Vérifie un token d'essai
   * @param token Token de vérification
   */
  verifyTrial: async (token: string): Promise<{ 
    success: boolean; 
    message: string; 
    accessToken?: string;
    prospectId?: string;
  }> => {
    try {
      // Vérifier si le token existe dans les métadonnées d'un prospect
      const { data, error } = await supabase
        .from('prospects')
        .select('id, site_url, nom, metadata')
        .eq('metadata->verification_token', token)
        .single();
      
      if (error || !data) {
        return {
          success: false,
          message: "Token invalide ou déjà utilisé"
        };
      }
      
      // Générer un nouveau token d'accès
      const accessToken = uuidv4();
      
      // Mettre à jour le statut du prospect
      const { error: updateError } = await supabase
        .from('prospects')
        .update({
          token: accessToken,
          updated_at: new Date().toISOString(),
          metadata: {
            ...data.metadata,
            status: 'verified'
          }
        })
        .eq('id', data.id);
        
      if (updateError) {
        console.error("Erreur lors de la mise à jour du prospect:", updateError);
        throw updateError;
      }
      
      // Lancer l'analyse automatique si nécessaire
      if (data.site_url) {
        const location = data.metadata?.location || '';
        AutoAnalysisService.runAllAnalyses(data.id, data.site_url, data.nom, location).catch(error => {
          console.error('Erreur lors du lancement des analyses automatiques:', error);
        });
      }
      
      return {
        success: true,
        message: "Vérification réussie. Redirection vers l'analyse...",
        accessToken,
        prospectId: data.id
      };
    } catch (error) {
      console.error("Erreur lors de la vérification de l'essai:", error);
      return {
        success: false,
        message: "Une erreur est survenue lors de la vérification. Veuillez réessayer."
      };
    }
  }
};

// Toutes les fonctions auxiliaires ont été intégrées directement dans les méthodes principales
// pour simplifier le code et éviter la duplication de logique
